// @ts-strict-ignore
import Spin from 'antd/lib/spin';
import * as React from 'react';
import { FetchableEntityType, FetchableEntityTypeV2 } from '../../entityType';
import { MehOutlined } from '@ant-design/icons';
import Result from 'antd/lib/result';
import { antGrey } from '../styles/globalStyles';
import Button from 'antd/lib/button';
import {
    selectIsDemoInstanceOrDemoModeEnabled,
    selectIsReleaseStepPreviouslyFetched,
    selectIsStepRunPreviouslyFetched,
    selectReleaseVersion,
    selectRealtimeRevalidationSequence,
    selectStepRunGraph,
    selectWorkflowEnabledSteps,
    selectWorkflowId,
    selectWorkflowRunId,
} from '../../selectors/selectors';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import { tryFetchEntity } from '../../actions/entities';
import { ReleaseStepType } from '../../types';
import {
    Nullable,
    ReleaseStepComponentProps,
    ReleaseStepTabRouteComponentMatchParams,
    StepRunComponentProps,
    TemporalStepRunComponentProps,
} from '../../typescriptHelpers';
import { generateBackwardsCompatibleReleaseStepRunId } from '@/reducers/releaseStep';
import { useError, useGetEntityV2, useIsLoading } from '@/utils/hooks/helpers';
import { flatMap } from 'lodash';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
    createReleaseOverviewRoute,
    createReleaseRolloutRoute,
    createReleaseStepRoute,
    createStepRunRoute,
    createStepRunsSummaryRoute,
} from '@/helpers/routeHelpers';

export const withReleaseStep = <P extends object>(
    WrappedComponent: React.ComponentType<
        P &
            ReleaseStepComponentProps &
            TemporalStepRunComponentProps & {
                stepRunComponentProps: Nullable<StepRunComponentProps>;
            }
    >,
) => {
    const ReleaseStepWrapper: React.FC<
        P &
            ReleaseStepComponentProps &
            TemporalStepRunComponentProps & {
                showLoader?: boolean;
                showError?: boolean;
                withTracking?: boolean;
            }
    > = (props) => {
        const { orgId, appId, releaseId, releaseStepType, stepRunId, showLoader = true, showError = true } = props;
        const [displayError, setDisplayError] = React.useState(false);
        const releaseStepId = generateBackwardsCompatibleReleaseStepRunId(releaseId, releaseStepType);

        const history = useHistory();
        const location = useLocation();
        const { releaseStepTabType } = useParams<ReleaseStepTabRouteComponentMatchParams>();

        const stepRunNodes = useSelector((state: RootState) => {
            const stepRunGraph = selectStepRunGraph(state, releaseId);

            return flatMap(stepRunGraph.stepGroups, (stepGroup) => {
                return flatMap(stepGroup.stepArchetypeGroups, (archetypeGroup) => {
                    return archetypeGroup.stepRunStatusDatum;
                });
            });
        });

        const matchingStepRunNode = stepRunNodes.find((stepRunNode) => {
            return stepRunNode.stepRunId === stepRunId;
        });

        const workflowRunId = useSelector((state: RootState) => selectWorkflowRunId(state, releaseId));

        const releaseVersion = useSelector((state: RootState) => selectReleaseVersion(state, releaseId));

        const workflowId = useSelector((state: RootState) => selectWorkflowId(state, appId));
        const realtimeRevalidationSequence = useSelector(selectRealtimeRevalidationSequence);
        const lastHandledRealtimeRevalidationSequence = React.useRef(realtimeRevalidationSequence);

        const stepRunComponentProps = React.useMemo<Nullable<StepRunComponentProps>>(
            () =>
                workflowRunId && workflowId && matchingStepRunNode ?
                    {
                        orgId,
                        appId,
                        releaseId,
                        workflowId,
                        workflowRunId,
                        stepConfigId: matchingStepRunNode.stepConfigId,
                        stepRunId: matchingStepRunNode.stepRunId,
                    }
                :   null,
            [appId, matchingStepRunNode, orgId, releaseId, workflowId, workflowRunId],
        );

        const hasStepRunProps = !!stepRunComponentProps;

        const [fetchStepRun, isLoadingStepRun, stepRunFetchError] = useGetEntityV2(
            FetchableEntityTypeV2.StepRun,
            matchingStepRunNode?.stepRunId,
        );
        const fetchStepRunRef = React.useRef(fetchStepRun);
        fetchStepRunRef.current = fetchStepRun;

        const dispatch = useDispatch();
        const isLoadingReleaseStep = useIsLoading(FetchableEntityType.ReleaseStep, releaseStepId);
        const errorFetchingReleaseStep = useError(FetchableEntityType.ReleaseStep, releaseStepId);

        const isLoading = hasStepRunProps ? isLoadingStepRun : isLoadingReleaseStep;
        const errorFetching = hasStepRunProps ? stepRunFetchError : errorFetchingReleaseStep;

        const isReleaseStepPreviouslyFetched = useSelector((state: RootState) =>
            selectIsReleaseStepPreviouslyFetched(state, releaseId, releaseStepType),
        );

        const isStepRunPreviouslyFetched = useSelector((state: RootState) =>
            selectIsStepRunPreviouslyFetched(state, stepRunComponentProps?.stepRunId),
        );

        const isPreviouslyFetched = hasStepRunProps ? isStepRunPreviouslyFetched : isReleaseStepPreviouslyFetched;

        const isDemoMode = useSelector((state: RootState) =>
            selectIsDemoInstanceOrDemoModeEnabled(state, orgId, releaseId),
        );

        const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, appId));
        const isTryingToAccessDeprecatedStep = !!workflowEnabledSteps[releaseStepType];
        const isTryingToAccessUnknownStepRun = !!stepRunId && !matchingStepRunNode;

        const deprecatedStepRedirect = useSelector((state: RootState) => {
            const stepRunGraph = selectStepRunGraph(state, releaseId);
            const isStepRunGraphLoaded = !!stepRunGraph;

            const target = {
                isStepRunGraphLoaded,
                groupIndex: null as number | null,
                archetypeGroupIndex: null as number | null,
                stepRunCount: 0,
                firstStepRunId: null as string | null,
            };

            if (releaseStepType && stepRunGraph) {
                const stepGroups = stepRunGraph.stepGroups ?? [];
                for (let groupIndex = 0; groupIndex < stepGroups.length; groupIndex++) {
                    const stepArchetypeGroups = stepGroups[groupIndex].stepArchetypeGroups;
                    const archetypeGroupIndex = stepArchetypeGroups.findIndex(
                        (archetypeGroup) => archetypeGroup.archetype === releaseStepType,
                    );
                    if (archetypeGroupIndex === -1) {
                        continue;
                    }

                    const stepRunIds = stepArchetypeGroups[archetypeGroupIndex].stepRunStatusDatum
                        .map((datum) => datum.stepRunId)
                        .filter((id): id is string => !!id);

                    // Keep the LAST matching archetype group. When an archetype spans multiple phases (e.g. a beta
                    // rollout early in the flow and the production rollout later) we want the final one, which is the
                    // production rollout the user expects to land on.
                    target.groupIndex = groupIndex;
                    target.archetypeGroupIndex = archetypeGroupIndex;
                    target.stepRunCount = stepRunIds.length;
                    target.firstStepRunId = stepRunIds[0] ?? null;
                }
            }

            return target;
        }, shallowEqual);

        const fetchCurrentStep = React.useCallback(() => {
            if (isDemoMode) {
                return;
            }

            if (stepRunComponentProps) {
                fetchStepRunRef.current(stepRunComponentProps);
            } else {
                dispatch(
                    tryFetchEntity(
                        FetchableEntityType.ReleaseStep,
                        { orgId, appId, releaseId, releaseStepType },
                        releaseStepId,
                    ),
                );
            }
        }, [appId, dispatch, isDemoMode, orgId, releaseId, releaseStepId, releaseStepType, stepRunComponentProps]);

        const fetchReleaseStep = React.useCallback(() => {
            setDisplayError(false);

            if (releaseStepType === ReleaseStepType.Rollout && !isTryingToAccessDeprecatedStep) {
                const legacyRolloutRoute = createReleaseRolloutRoute(orgId, appId, releaseVersion, releaseStepTabType);
                history.replace({ pathname: legacyRolloutRoute, search: location.search });
                return;
            }

            if (isTryingToAccessDeprecatedStep || isTryingToAccessUnknownStepRun) {
                if (isTryingToAccessDeprecatedStep && releaseStepType) {
                    const { groupIndex, archetypeGroupIndex, stepRunCount, firstStepRunId } = deprecatedStepRedirect;

                    if (stepRunCount > 1) {
                        const summaryRoute = createStepRunsSummaryRoute(
                            orgId,
                            appId,
                            releaseVersion,
                            groupIndex,
                            archetypeGroupIndex,
                        );
                        history.replace({ pathname: summaryRoute, search: location.search });
                        return;
                    }

                    if (firstStepRunId) {
                        const fromReleaseStepPath = createReleaseStepRoute(
                            orgId,
                            appId,
                            releaseVersion,
                            releaseStepType,
                        );
                        const toStepRunPath = createStepRunRoute(orgId, appId, releaseVersion, firstStepRunId);
                        const rewrittenPathname = location.pathname.replace(fromReleaseStepPath, toStepRunPath);
                        history.replace({ pathname: rewrittenPathname, search: location.search });
                        return;
                    }
                }

                if (deprecatedStepRedirect.isStepRunGraphLoaded) {
                    const releaseOverviewRoute = createReleaseOverviewRoute(orgId, appId, releaseVersion);
                    history.replace(releaseOverviewRoute);
                }
                return;
            }

            fetchCurrentStep();
        }, [
            appId,
            deprecatedStepRedirect,
            fetchCurrentStep,
            history,
            isTryingToAccessDeprecatedStep,
            isTryingToAccessUnknownStepRun,
            location.pathname,
            location.search,
            orgId,
            releaseStepTabType,
            releaseStepType,
            releaseVersion,
        ]);

        React.useEffect(() => {
            fetchReleaseStep();
        }, [fetchReleaseStep]);

        // Refetch without rerunning deprecated-step redirects.
        React.useEffect(() => {
            if (lastHandledRealtimeRevalidationSequence.current === realtimeRevalidationSequence) {
                return;
            }
            lastHandledRealtimeRevalidationSequence.current = realtimeRevalidationSequence;
            setDisplayError(false);
            fetchCurrentStep();
        }, [fetchCurrentStep, realtimeRevalidationSequence]);

        React.useEffect(() => {
            if (errorFetching && !isPreviouslyFetched) {
                setDisplayError(true);
            }
        }, [errorFetching, isPreviouslyFetched]);

        return (
            <>
                {isLoading && !isPreviouslyFetched && showLoader && (
                    <div
                        style={{
                            display: 'flex',
                            flexGrow: 100,
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Spin size="small" />
                    </div>
                )}
                {displayError && showError && (
                    <div
                        style={{
                            display: 'flex',
                            flexGrow: 1,
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Result
                            icon={<MehOutlined style={{ color: antGrey.grey5 }} />}
                            subTitle="Error fetching release step"
                            extra={<Button onClick={fetchReleaseStep}>Try again</Button>}
                        />
                    </div>
                )}
                {isPreviouslyFetched && (
                    <WrappedComponent
                        {...props}
                        stepRunComponentProps={stepRunComponentProps}
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        releaseStepType={releaseStepType}
                    />
                )}
            </>
        );
    };

    // Set display name for easier debugging
    const wrappedComponentName = WrappedComponent.displayName || WrappedComponent.name || 'Component';
    ReleaseStepWrapper.displayName = `withReleaseStep(${wrappedComponentName})`;

    return ReleaseStepWrapper;
};
